/**
 * Client for interacting with robot via SSH/SCP through backend API
 */
export class RobotClient {
  constructor(apiBaseUrl = null) {
    // Auto-detect: use Vercel serverless functions in production, local server in dev
    this.apiBaseUrl = apiBaseUrl || this.getApiBaseUrl();
    this.credentials = null;
  }

  /**
   * Determine API base URL based on environment
   */
  getApiBaseUrl() {
    // Check for environment variable first (set in Vercel or .env)
    if (import.meta.env.VITE_API_BASE_URL) {
      return import.meta.env.VITE_API_BASE_URL;
    }

    // Fallback: In development, use localhost:3001
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    }

    // Production without env var: return empty (won't work but will be obvious)
    console.warn('VITE_API_BASE_URL not set. Robot features will not work.');
    return '';
  }

  /**
   * Store credentials for reuse
   */
  setCredentials(host, username, password) {
    this.credentials = { host, username, password };
  }

  /**
   * Get stored credentials
   */
  getCredentials() {
    return this.credentials;
  }

  /**
   * Check if backend server is available
   */
  async checkServerAvailability() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/robot/health`, {
        method: 'GET',
        signal: AbortSignal.timeout(2000) // 2 second timeout
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  }

  /**
   * List all .urp files on the robot
   */
  async listFiles(host, username, password) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/robot/list-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ host, username, password }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to list files');
      }

      const data = await response.json();
      this.setCredentials(host, username, password);
      return data.files;
    } catch (error) {
      console.error('Error listing files:', error);
      if (error.message.includes('fetch')) {
        throw new Error('Backend server not available. Robot features require the backend server to be running.');
      }
      throw error;
    }
  }

  /**
   * Download a specific .urp file from the robot
   */
  async downloadFile(filename, host, username, password) {
    try {
      const creds = this.credentials || { host, username, password };
      
      const response = await fetch(`${this.apiBaseUrl}/api/robot/download-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host: creds.host,
          username: creds.username,
          password: creds.password,
          filename,
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to download file');
      }

      const data = await response.json();
      
      // Convert base64 back to binary
      const binaryData = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
      
      return { filename: data.filename, data: binaryData };
    } catch (error) {
      console.error('Error downloading file:', error);
      throw error;
    }
  }

  /**
   * Upload a .urp file to the robot
   */
  async uploadFile(filename, fileData, host, username, password) {
    try {
      const creds = this.credentials || { host, username, password };
      
      // Convert binary data to base64
      let base64Data;
      if (fileData instanceof Uint8Array) {
        base64Data = btoa(String.fromCharCode(...fileData));
      } else if (fileData instanceof ArrayBuffer) {
        base64Data = btoa(String.fromCharCode(...new Uint8Array(fileData)));
      } else {
        throw new Error('Invalid file data format');
      }

      const response = await fetch(`${this.apiBaseUrl}/api/robot/upload-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host: creds.host,
          username: creds.username,
          password: creds.password,
          filename,
          data: base64Data,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload file');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Download installation file from robot
   */
  async downloadInstallation(filename, host, username, password) {
    try {
      const creds = this.credentials || { host, username, password };

      const response = await fetch(`${this.apiBaseUrl}/api/robot/download-installation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          host: creds.host,
          username: creds.username,
          password: creds.password,
          filename,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to download installation file');
      }

      const data = await response.json();
      const binaryData = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
      return { filename: data.filename, data: binaryData };
    } catch (error) {
      console.error('Error downloading installation:', error);
      throw error;
    }
  }

  /**
   * FTP: List all .urp files on the robot
   */
  async listFilesFTP() {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/robot/ftp/list-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to list files via FTP');
      }

      const data = await response.json();
      return data.files;
    } catch (error) {
      console.error('Error listing files via FTP:', error);
      if (error.message.includes('fetch')) {
        throw new Error('Backend server not available. Robot features require the backend server to be running.');
      }
      throw error;
    }
  }

  /**
   * FTP: Download a specific .urp file from the robot
   */
  async downloadFileFTP(filename) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/robot/ftp/download-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to download file via FTP');
      }

      const data = await response.json();

      // Convert base64 back to binary
      const binaryData = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));

      return { filename: data.filename, data: binaryData };
    } catch (error) {
      console.error('Error downloading file via FTP:', error);
      throw error;
    }
  }

  /**
   * FTP: Upload a .urp file to the robot
   */
  async uploadFileFTP(filename, fileData) {
    try {
      // Convert binary data to base64
      let base64Data;
      if (fileData instanceof Uint8Array) {
        base64Data = btoa(String.fromCharCode(...fileData));
      } else if (fileData instanceof ArrayBuffer) {
        base64Data = btoa(String.fromCharCode(...new Uint8Array(fileData)));
      } else {
        throw new Error('Invalid file data format');
      }

      const response = await fetch(`${this.apiBaseUrl}/api/robot/ftp/upload-file`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filename,
          data: base64Data,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to upload file via FTP');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error uploading file via FTP:', error);
      throw error;
    }
  }

  /**
   * FTP: Download installation file from robot
   */
  async downloadInstallationFTP(filename) {
    try {
      const response = await fetch(`${this.apiBaseUrl}/api/robot/ftp/download-installation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to download installation file via FTP');
      }

      const data = await response.json();
      const binaryData = Uint8Array.from(atob(data.data), c => c.charCodeAt(0));
      return { filename: data.filename, data: binaryData };
    } catch (error) {
      console.error('Error downloading installation via FTP:', error);
      throw error;
    }
  }
}
